import { useCallback, useEffect, useState } from "react";
import { GitBranch } from "lucide-react";

import {
  createEquipmentDependency,
  deleteEquipmentDependency,
  listEquipment,
  listEquipmentDependencies,
  type Equipment,
  type EquipmentDependency,
} from "@/lib/api/building";
import { SectionPanel } from "@/components/ui/section-panel";
import { cardSubtle } from "@/lib/card-styles";
import { cn } from "@/lib/utils";

interface Props {
  buildingId: string;
  // Create/remove is a structural reconfiguration — configurator-only (auth.ts). The list is
  // read-only for everyone else.
  configurator: boolean;
  // Bump to re-fetch in step with sibling structure changes (e.g. an equipment item deleted).
  refreshKey?: number;
  onChanged?: () => void;
}

// Configurator editor for equipment dependency topology (Flow 1.6, upstream→downstream `feeds`),
// reusing the dedicated building endpoints. The backend guards self-dependencies and cycles; those
// rejections are surfaced here rather than swallowed.
export function DependencyTopologyEditor({
  buildingId,
  configurator,
  refreshKey = 0,
  onChanged,
}: Props) {
  const [equipment, set_equipment] = useState<Equipment[]>([]);
  const [deps, set_deps] = useState<EquipmentDependency[]>([]);
  const [loading, set_loading] = useState(true);
  const [source_id, set_source_id] = useState("");
  const [target_id, set_target_id] = useState("");
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [eq, dp] = await Promise.all([
      listEquipment(buildingId),
      listEquipmentDependencies(buildingId).catch(
        (): EquipmentDependency[] => [],
      ),
    ]);
    set_equipment(eq);
    set_deps(dp);
    set_loading(false);
  }, [buildingId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const name_of = (id: string) =>
    equipment.find((e) => e.id === id)?.name ?? id;

  async function handle_create() {
    if (!source_id || !target_id) return;
    set_busy(true);
    set_error(null);
    try {
      await createEquipmentDependency(buildingId, {
        source_equipment_id: source_id,
        target_equipment_id: target_id,
        dependency_type: "feeds",
      });
      set_source_id("");
      set_target_id("");
      await load();
      onChanged?.();
    } catch (e) {
      set_error(
        e instanceof Error ? e.message : "Riippuvuuden luonti epäonnistui.",
      );
    } finally {
      set_busy(false);
    }
  }

  async function handle_remove(depId: string) {
    set_busy(true);
    set_error(null);
    try {
      await deleteEquipmentDependency(buildingId, depId);
      await load();
      onChanged?.();
    } catch (e) {
      set_error(
        e instanceof Error ? e.message : "Riippuvuuden poisto epäonnistui.",
      );
    } finally {
      set_busy(false);
    }
  }

  return (
    <SectionPanel
      icon={GitBranch}
      title="Riippuvuudet"
      subtitle="Talotekniikan syöttösuhteet (feeds) — käytetään havaintojen topologiakontekstissa"
    >
      {loading ? (
        <p className="text-sm text-muted-foreground">Ladataan…</p>
      ) : (
        <div className="space-y-3">
          {deps.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ei riippuvuuksia.</p>
          ) : (
            <ul className="space-y-1.5">
              {deps.map((d) => (
                <li
                  key={d.id}
                  className={cn(
                    cardSubtle,
                    "flex items-center gap-2 px-3 py-2 text-sm",
                  )}
                >
                  <span className="font-medium">
                    {name_of(d.source_equipment_id)}
                  </span>
                  <span className="text-muted-foreground">→</span>
                  <span className="font-medium">
                    {name_of(d.target_equipment_id)}
                  </span>
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
                    {d.dependency_type}
                  </span>
                  {configurator && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void handle_remove(d.id)}
                      title="Poista riippuvuus"
                      className="ml-auto text-muted-foreground hover:text-destructive disabled:opacity-50"
                    >
                      ✕
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {configurator &&
            (equipment.length >= 2 ? (
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Lähde
                  </label>
                  <select
                    value={source_id}
                    disabled={busy}
                    onChange={(e) => set_source_id(e.target.value)}
                    className="rounded border border-border/40 bg-background px-2 py-1.5 text-sm outline-none focus:border-primary/60 disabled:opacity-50"
                  >
                    <option value="">Valitse…</option>
                    {equipment.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name}
                      </option>
                    ))}
                  </select>
                </div>
                <span className="pb-2 text-muted-foreground">→</span>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Kohde
                  </label>
                  <select
                    value={target_id}
                    disabled={busy}
                    onChange={(e) => set_target_id(e.target.value)}
                    className="rounded border border-border/40 bg-background px-2 py-1.5 text-sm outline-none focus:border-primary/60 disabled:opacity-50"
                  >
                    <option value="">Valitse…</option>
                    {equipment.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  disabled={busy || !source_id || !target_id}
                  onClick={() => void handle_create()}
                  className="rounded bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50"
                >
                  {busy ? "Tallennetaan…" : "Lisää riippuvuus"}
                </button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Riippuvuuden luonti vaatii vähintään kaksi taloteknistä
                laitetta.
              </p>
            ))}

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      )}
    </SectionPanel>
  );
}
